import React, { useState, useEffect } from "react";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";
import { ApnsConfiguration } from "@/types/apns";
import { ApnsConfigurationCard } from "./ApnsConfigurationCard";
import { ApnsConfigurationDialog } from "./ApnsConfigurationDialog";
import { toast } from "sonner";
import { Plus, Shield } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import * as tenantsApi from "@/services/tenantsApi";

export function ApnsConfigurationList() {
  const [configurations, setConfigurations] = useState<ApnsConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ApnsConfiguration | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<ApnsConfiguration | null>(null);

  const loadConfigurations = async () => {
    setIsLoading(true);
    try {
      const info = await tenantsApi.getApnsCertInfo();
      if (info && typeof info === 'object' && Object.keys(info).length > 0) {
        const raw = info as Record<string, any>;
        // Map actual API response shape to ApnsConfiguration
        const mapped: ApnsConfiguration = {
          id: raw.id || 'apns-cert',
          name: raw.apns_topic || raw.name || 'APNs Certificate',
          organization_name: raw.organization_name || raw.organizationName || '',
          common_name: raw.common_name || raw.commonName || raw.apns_topic || '',
          country_code: raw.country_code || raw.countryCode || '',
          private_key_encrypted: '',
          csr: '',
          csr_generated_at: raw.csr_generated_at || raw.csrGeneratedAt || raw.updated_at || '',
          certificate_expires_at: raw.apns_expiry_at || raw.certificate_expires_at || raw.certificateExpiresAt,
          status: raw.status || (raw.apns_expiry_at ? 'active' : 'csr_generated'),
          created_at: raw.created_at || raw.createdAt || raw.updated_at || '',
          updated_at: raw.updated_at || raw.updatedAt || '',
          customer_id: raw.customer_id || raw.customerId,
        };
        setConfigurations([mapped]);
      } else {
        setConfigurations([]);
      }
    } catch { setConfigurations([]); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { loadConfigurations(); }, []);

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;
    try {
      await tenantsApi.deleteApnsCert();
      toast.success("APNs configuration deleted");
      loadConfigurations();
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete APNs configuration");
    } finally { setDeleteDialogOpen(false); setConfigToDelete(null); }
  };

  if (isLoading) return <div className="text-center py-8 text-muted-foreground">Loading APNs configurations...</div>;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="APNs Certificates"
        description="Manage Apple Push Notification service certificates"
        buttonLabel="Add APNs Configuration"
        onButtonClick={() => { setEditingConfig(null); setDialogOpen(true); }}
      />
      {configurations.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold">No APNs Certificates Configured</h3>
          <p className="text-sm text-muted-foreground">APNs certificates are required for push notifications to Apple devices.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {configurations.map((config) => <ApnsConfigurationCard key={config.id} config={config} onEdit={() => { setEditingConfig(config); setDialogOpen(true); }} onDelete={() => { setConfigToDelete(config); setDeleteDialogOpen(true); }} />)}
        </div>
      )}
      <ApnsConfigurationDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={loadConfigurations} initialData={editingConfig} />
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete APNs Configuration</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete this APNs configuration? Devices will no longer receive push notifications. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
